<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dateFormat } from 'shared/helpers/timeHelper';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { getCountryOptions } from 'dashboard/helper/countries';

const props = defineProps({
  contactReasonPath: {
    type: Array,
    default: () => [],
  },
  contactReason: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['updated']);

const store = useStore();
const { t } = useI18n();

const reasonName = ref('');
const reasonDescription = ref('');
const reasonColor = ref('#9f9f9f');
const reasonStatus = ref(true);
const targetMarkets = ref([]);
const targetInboxes = ref([]);
const sopUrls = ref([]);
const allCountryOptions = getCountryOptions();
const relatedAgents = ref({ creator: null, updator: null });

const rules = {
  reasonName: { required },
  reasonDescription: { required },
};
const v$ = useVuelidate(rules, {
  reasonName,
  reasonDescription,
});

const uiFlags = useMapGetter('contactReasons/getUIFlags');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');

const hasNoChildren = computed(
  () => !props.contactReason?.childReasons?.length
);

const marketOptions = computed(() => {
  return [...new Set(inboxes.value.map(inbox => inbox.market))];
});

const inboxesOptions = computed(() => inboxes.value);

const marketMenuItems = computed(() =>
  marketOptions.value.map(m => ({ value: m, label: m }))
);

const inboxMenuItems = computed(() =>
  inboxesOptions.value.map(i => ({ value: i.id, label: i.name }))
);

const editContactReason = async () => {
  try {
    const updatedContactReason = await store.dispatch('contactReasons/update', {
      contactReasonPath: props.contactReasonPath,
      data: {
        id: props.contactReason.id,
        name: reasonName.value,
        description: reasonDescription.value,
        active: reasonStatus.value,
        config: {
          color: reasonColor.value,
          target_markets: targetMarkets.value,
          target_inboxes: targetInboxes.value,
          sop_urls: hasNoChildren.value
            ? sopUrls.value.filter(e => e.market && e.url)
            : null,
        },
      },
    });
    emit('updated', updatedContactReason);
    useAlert(t('CONTACT_REASONS.API.UPDATE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACT_REASONS.API.UPDATE.ERROR_MESSAGE'));
  }
};

const loadContactReasonDetails = targetContactReason => {
  reasonName.value = targetContactReason.name || '';
  reasonDescription.value = targetContactReason.description || '';
  reasonStatus.value = targetContactReason.active ?? true;
  reasonColor.value = targetContactReason.config?.color || reasonColor.value;
  targetMarkets.value = targetContactReason.config?.targetMarkets || [];
  targetInboxes.value = targetContactReason.config?.targetInboxes || [];
  sopUrls.value = targetContactReason.config?.sopUrls || [];
};

const activeSettingsTab = ref(0);
const settingsTabs = computed(() => [
  { label: t('CONTACT_REASONS.FORM.TABS.GENERAL') },
  { label: t('CONTACT_REASONS.FORM.TABS.MARKET') },
]);
const onTabChanged = tab => {
  activeSettingsTab.value = settingsTabs.value.findIndex(
    s => s.label === tab.label
  );
};

const addSopUrl = () => sopUrls.value.push({ market: '', url: '' });
const removeSopUrl = index => sopUrls.value.splice(index, 1);

const loadRelatedAgents = () => {
  const creatorId = props.contactReason.createdById;
  const updaterId = props.contactReason.updatedById;
  const filteredAgents = agents.value.filter(
    agent => agent.id === creatorId || agent.id === updaterId
  );
  const structuredAgents = filteredAgents.map(agent => {
    return {
      id: agent.id,
      agent: agent.available_name || agent.name,
      email: agent.email,
      thumbnail: agent.thumbnail,
    };
  });

  relatedAgents.value = {
    creator: structuredAgents.find(agent => agent.id === creatorId),
    updater: structuredAgents.find(agent => agent.id === updaterId),
  };
};

onMounted(async () => {
  await store.dispatch('agents/get');
  loadContactReasonDetails(props.contactReason);
  loadRelatedAgents();
});

watch(
  () => props.contactReason,
  newReason => {
    if (newReason !== null) {
      loadContactReasonDetails(newReason);
      loadRelatedAgents();
    }
  }
);
</script>

<template>
  <div
    class="bg-n-solid-2 outline outline-1 outline-n-container rounded-xl px-8 py-6"
  >
    <div v-if="contactReason" class="flex flex-col gap-4">
      <h2 class="text-xl">{{ contactReason.name }}</h2>

      <!-- Metadata grid — original layout -->
      <div
        class="grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 text-sm text-n-slate-11 items-center"
      >
        <template v-if="relatedAgents.creator">
          <span>{{ $t('CONTACT_REASONS.DETAILS.CREATED_AT') }}</span>
          <div class="flex items-center justify-between gap-4">
            <span>{{
              dateFormat(contactReason.createdAt, 'h:mm a, MMM d, yyyy')
            }}</span>
            <div class="flex items-center">
              <Avatar
                :src="relatedAgents.creator.thumbnail"
                :name="relatedAgents.creator.agent"
                :size="28"
                hide-offline-status
                rounded-full
              />
              <div class="items-start flex flex-col min-w-0 my-0 mx-2">
                <h6
                  class="overflow-hidden text-xs m-0 leading-[1.2] text-n-slate-12 whitespace-nowrap text-ellipsis"
                >
                  {{ relatedAgents.creator.agent }}
                </h6>
                <span class="text-xs text-n-slate-11">
                  {{ relatedAgents.creator.email }}
                </span>
              </div>
            </div>
          </div>
        </template>
        <template v-if="relatedAgents.updater">
          <span>{{ $t('CONTACT_REASONS.DETAILS.UPDATED_AT') }}</span>
          <div class="flex items-center justify-between gap-4">
            <span>{{
              dateFormat(contactReason.updatedAt, 'h:mm a, MMM d, yyyy')
            }}</span>
            <div class="flex items-center">
              <Avatar
                :src="relatedAgents.updater.thumbnail"
                :name="relatedAgents.updater.agent"
                :size="28"
                hide-offline-status
                rounded-full
              />
              <div class="items-start flex flex-col min-w-0 my-0 mx-2">
                <h6
                  class="overflow-hidden text-xs m-0 leading-[1.2] text-n-slate-12 whitespace-nowrap text-ellipsis"
                >
                  {{ relatedAgents.updater.agent }}
                </h6>
                <span class="text-xs text-n-slate-11">
                  {{ relatedAgents.updater.email }}
                </span>
              </div>
            </div>
          </div>
        </template>
        <span>{{ $t('CONTACT_REASONS.DETAILS.TIMES_USED') }}</span>
        <span>{{ contactReason.conversationsCount }}</span>
      </div>

      <!-- Settings section with tabs -->
      <div class="border-t border-n-container pt-4">
        <div class="flex items-center justify-between mb-4">
          <p class="text-base text-n-brand font-medium">
            {{ $t('CONTACT_REASONS.DETAILS.SETTINGS') }}
          </p>
          <TabBar
            :tabs="settingsTabs"
            :initial-active-tab="activeSettingsTab"
            @tab-changed="onTabChanged"
          />
        </div>

        <form
          class="flex flex-col items-start w-full"
          @submit.prevent="editContactReason"
        >
          <!-- General tab -->
          <template v-if="activeSettingsTab === 0">
            <div class="w-full mb-2">
              <label>
                {{ $t('CONTACT_REASONS.FORM.ACTIVE.TITLE') }}
                <select v-model="reasonStatus" class="w-full mt-1">
                  <option :value="true">
                    {{ $t('CONTACT_REASONS.FORM.ACTIVE.ENABLED') }}
                  </option>
                  <option :value="false">
                    {{ $t('CONTACT_REASONS.FORM.ACTIVE.DISABLED') }}
                  </option>
                </select>
              </label>
            </div>
            <div class="w-full">
              <label :class="{ error: v$.reasonName.$error }">
                {{ $t('CONTACT_REASONS.FORM.NAME.LABEL') }}
                <input
                  v-model="reasonName"
                  type="text"
                  :placeholder="$t('CONTACT_REASONS.FORM.NAME.PLACEHOLDER')"
                  @input="v$.reasonName.$touch"
                />
              </label>
            </div>
            <div class="w-full">
              <label :class="{ error: v$.reasonDescription.$error }">
                {{ $t('CONTACT_REASONS.FORM.DESCRIPTION.LABEL') }}
                <input
                  v-model="reasonDescription"
                  type="text"
                  :placeholder="
                    $t('CONTACT_REASONS.FORM.DESCRIPTION.PLACEHOLDER')
                  "
                  @input="v$.reasonDescription.$touch"
                />
              </label>
            </div>
            <div class="w-full">
              <label>
                {{ $t('CONTACT_REASONS.FORM.COLOR.LABEL') }}
                <woot-color-picker v-model="reasonColor" />
              </label>
            </div>
          </template>

          <!-- Market tab -->
          <template v-else>
            <div class="w-full">
              <label>
                {{ $t('CONTACT_REASONS.FORM.TARGET_MARKETS.LABEL') }}
                <TagMultiSelectComboBox
                  v-model="targetMarkets"
                  :options="marketMenuItems"
                  :placeholder="
                    $t('CONTACT_REASONS.FORM.TARGET_MARKETS.PLACEHOLDER')
                  "
                />
              </label>
            </div>
            <div class="w-full">
              <label>
                {{ $t('CONTACT_REASONS.FORM.TARGET_INBOXES.LABEL') }}
                <TagMultiSelectComboBox
                  v-model="targetInboxes"
                  :options="inboxMenuItems"
                  :placeholder="
                    $t('CONTACT_REASONS.FORM.TARGET_INBOXES.PLACEHOLDER')
                  "
                />
              </label>
            </div>
            <div v-if="hasNoChildren" class="w-full">
              <label class="block mb-1">
                {{ $t('CONTACT_REASONS.FORM.SOP_URLS.LABEL') }}
              </label>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(entry, index) in sopUrls"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <div class="w-44 shrink-0">
                    <ComboBox
                      v-model="entry.market"
                      :options="allCountryOptions"
                      :placeholder="
                        $t('CONTACT_REASONS.FORM.SOP_URLS.MARKET_PLACEHOLDER')
                      "
                      :search-placeholder="
                        $t('CONTACT_REASONS.FORM.SOP_URLS.MARKET_SEARCH')
                      "
                    />
                  </div>
                  <input
                    v-model="entry.url"
                    type="text"
                    class="flex-1 !mb-0"
                    :placeholder="
                      $t('CONTACT_REASONS.FORM.SOP_URLS.URL_PLACEHOLDER')
                    "
                  />
                  <Button
                    icon="i-lucide-trash-2"
                    faded
                    slate
                    type="button"
                    @click="removeSopUrl(index)"
                  />
                </div>
              </div>
              <Button
                icon="i-lucide-plus"
                faded
                slate
                type="button"
                size="small"
                :label="$t('CONTACT_REASONS.FORM.SOP_URLS.ADD')"
                class="mt-2"
                @click="addSopUrl"
              />
              <p class="text-xs text-n-slate-11 mt-1">
                {{ $t('CONTACT_REASONS.FORM.SOP_URLS.HELPER') }}
              </p>
            </div>
            <div v-else class="w-full py-8 flex flex-col items-center gap-2">
              <p class="text-sm text-n-slate-11">
                {{ $t('CONTACT_REASONS.FORM.SOP_URLS.PARENT_NOTE') }}
              </p>
            </div>
          </template>

          <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
            <Button
              type="submit"
              :label="$t('CONTACT_REASONS.FORM.UPDATE')"
              :disabled="v$.$invalid || uiFlags.isUpdating"
              :is-loading="uiFlags.isUpdating"
            />
          </div>
        </form>
      </div>
    </div>

    <div v-else class="flex flex-col items-center justify-center gap-2 py-24">
      <Icon icon="i-lucide-package-search" class="size-16 text-n-slate-11" />
      <p class="text-xl font-semibold">
        {{ $t('CONTACT_REASONS.DETAILS.EMPTY_STATE.TITLE') }}
      </p>
      <p class="-mt-2">
        {{ $t('CONTACT_REASONS.DETAILS.EMPTY_STATE.DESCRIPTION') }}
      </p>
    </div>
  </div>
</template>
